<script setup lang="ts" generic="T extends unknown">
import Group from '../../components/Group/Group.vue'
import Button from '../Button/Button.vue'
import { FontSize } from '../Button/types'

type Options = {
  value: T
  disabled?: boolean
}

// props
type Props = {
  options: Options[]
  withSeparator?: boolean
  withBorder?: boolean
  variant?: 'default' | 'flat'
  width?: 'full'
  fontSize?: FontSize
  roundedEdges?: boolean
  buttonClass?: string
}

withDefaults(defineProps<Props>(), {
  variant: 'default',
})

// data
const selectedIndex = defineModel<number>()

// methods
function selectIndex(index: number): void {
  selectedIndex.value = index
}
</script>

<template>
  <div
    v-if="options.length"
    class="flex overflow-hidden"
    :class="{
      'space-x-0.5': withSeparator && options.length === 2,
      'bg-neutral-light-8':
        withSeparator && selectedIndex === undefined && options.length === 2,
      'bg-transparent':
        withSeparator && selectedIndex !== undefined && options.length === 2,
      'rounded-sm': variant === 'default',
    }"
  >
    <Group
      :optionsCount="options.length"
      :selectedOption="
        selectedIndex === undefined ? undefined : selectedIndex + 1
      "
    >
      <template #default="{ option, selected }">
        <div
          class="*:w-full"
          :class="{
            '*:border-none': !withBorder,
            'w-full': width === 'full',
          }"
          @click="selectIndex(option - 1)"
        >
          <slot
            :option="options[option - 1]"
            :selected
          >
            <Button
              class="text-neutral-dark-0 leading-3 text-wrap"
              :class="[
                {
                  'cursor-pointer': !options[option - 1].disabled,
                  'bg-neutral-light-0': selected,
                  'bg-neutral-light-7 hover:bg-neutral-light-3 active:bg-neutral-light-0':
                    !selected && !options[option - 1].disabled,
                  'rounded-r-none':
                    (selected && option === 1 && !roundedEdges) ||
                    (option === 1 && roundedEdges),
                  'rounded-l-none':
                    (selected && option === options.length && !roundedEdges) ||
                    (option === options.length && roundedEdges),
                  'rounded-r-none border-r-0':
                    !selected && withBorder && option === 1 && !roundedEdges,
                  'rounded-l-none border-l-0':
                    !selected &&
                    withBorder &&
                    option === options.length &&
                    !roundedEdges,
                  'rounded-none!':
                    (roundedEdges &&
                      option !== 1 &&
                      option !== options.length) ||
                    !withBorder ||
                    (variant === 'flat' && !roundedEdges),
                  'border-neutral-light-0 border-[1px]': withBorder,
                  'font-bold':
                    variant === 'flat' || (variant === 'default' && selected),
                },
                buttonClass,
              ]"
              size="md"
              :fontSize
              :disabled="options[option - 1].disabled"
            >
              {{ options[option - 1].value }}
            </Button>
          </slot>
        </div>
      </template>
    </Group>
  </div>
</template>
